/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import api from '@/lib/api';
import { Loader2, User as UserIcon, Globe, ExternalLink } from 'lucide-react';
import { BookCard } from '@/pages/Livros';

export default function AuthorDetail({ previewSource }) {
  const { slug, authorId } = useParams();
  const navigate = useNavigate();
  const [author, setAuthor] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    (async () => {
      try {
        if (previewSource === 'admin' && authorId) {
          const { data } = await api.get(`/admin/livros/preview/author/${authorId}`);
          setAuthor(data);
          setStatus('ready');
          return;
        }
        const sec = await api.get('/livros/section-status');
        if (sec.data.status !== 'published') {
          setStatus('unavailable');
          return;
        }
        const { data } = await api.get(`/livros/autores/${slug}`);
        if (data.slug && data.slug !== slug) { navigate(`/livros/autores/${data.slug}`, { replace: true }); return; }
        setAuthor(data);
        setStatus('ready');
      } catch {
        setStatus('unavailable');
      }
    })();
  }, [slug, authorId, previewSource, navigate]);

  if (status === 'loading') return <div className="min-h-screen bg-background"><Navbar /><div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-ruby" /></div></div>;
  if (status !== 'ready' || !author) {
    return (
      <>
        <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
        <div className="min-h-screen bg-background"><Navbar />
          <div className="max-w-lg mx-auto pt-40 px-4 text-center">
            <h1 className="text-2xl font-bold text-foreground mb-2">Autor não disponível</h1>
            <button onClick={() => navigate('/livros')} className="mt-6 px-4 py-2 bg-ruby rounded-md text-white">Ver Livros</button>
          </div>
        </div>
      </>
    );
  }

  const isPreview = previewSource === 'admin';
  return (
    <>
      <Helmet>
        {isPreview && <meta name="robots" content="noindex, nofollow" />}
        <title>{author.name} — Intimate</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="max-w-5xl mx-auto px-4 pt-24 pb-16" data-testid="author-detail">
          {isPreview && (
            <div className="mb-6 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-300 px-4 py-2 text-sm">
              👁️ Pré-visualização — status: <b>{author.status}</b>. Não indexável.
            </div>
          )}
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="w-32 h-32 rounded-full overflow-hidden bg-white/5 shrink-0">
              {author.photo ? <img src={author.photo} alt={author.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><UserIcon className="w-12 h-12 text-muted-foreground" /></div>}
            </div>
            <div className="flex-1">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground">{author.name}</h1>
              {author.biography && <p className="text-muted-foreground mt-3 leading-relaxed whitespace-pre-line">{author.biography}</p>}
              <div className="mt-4 flex flex-wrap gap-3 text-sm">
                {author.website && <a href={author.website} target="_blank" rel="noopener noreferrer" className="text-ruby hover:underline inline-flex items-center gap-1"><Globe className="w-3.5 h-3.5" />Website</a>}
                {Object.entries(author.social || {}).filter(([, v]) => v).map(([k, v]) => (
                  <a key={k} href={v} target="_blank" rel="noopener noreferrer" className="text-ruby hover:underline inline-flex items-center gap-1 capitalize"><ExternalLink className="w-3.5 h-3.5" />{k}</a>
                ))}
              </div>
            </div>
          </div>

          {author.books?.length > 0 && (
            <section className="mt-10">
              <h2 className="text-xl font-semibold text-foreground mb-4">Livros publicados</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {author.books.map(b => <BookCard key={b.id} book={b} adminBadge={isPreview ? b.status : null} />)}
              </div>
            </section>
          )}
        </main>
      </div>
    </>
  );
}
